import type { Firestore } from "firebase/firestore";
import {
  type QueryDocumentSnapshot,
  Timestamp,
  getDoc,
  getDocs,
  limit as limitTo,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";

import { db } from "../../services/firebase.js";
import {
  type SmileSimulationEntity,
  SmileSimulationEntitySchema,
} from "./application.js";
import {
  smileSimulationCollection,
  smileSimulationDoc,
  smileSimulationDocPath,
} from "./refs.js";
import {
  type SmileSimulation,
  type SmileSimulationData,
  SmileSimulationDataSchema,
  SmileSimulationSchema,
  SmileSimulationUpdateDataSchema,
} from "./schema.js";

const toSmileSimulationEntity = (
  snapshot: QueryDocumentSnapshot<SmileSimulation>,
): SmileSimulationEntity =>
  SmileSimulationEntitySchema.parse({
    id: snapshot.id,
    ...snapshot.data(),
  });

const parseRequiredId = (fieldName: string, value: string) => {
  const parsedValue = value.trim();

  if (!parsedValue) {
    throw new Error(`${fieldName} is required.`);
  }

  return parsedValue;
};

const parseLimit = (value?: number) => {
  if (value === undefined) {
    return undefined;
  }

  if (!Number.isInteger(value) || value < 1) {
    throw new Error("Smile simulation list limit must be a positive integer.");
  }

  return value;
};

export const getSmileSimulation = async (
  clinicId: string,
  simulationId: string,
  firestore: Firestore = db,
): Promise<SmileSimulationEntity | null> => {
  const parsedClinicId = parseRequiredId("clinicId", clinicId);
  const parsedSimulationId = parseRequiredId("simulationId", simulationId);
  const snapshot = await getDoc(
    smileSimulationDoc(firestore, parsedClinicId, parsedSimulationId),
  );

  if (!snapshot.exists()) {
    return null;
  }

  return toSmileSimulationEntity(snapshot);
};

export const createSmileSimulation = async (
  clinicId: string,
  simulationId: string,
  data: SmileSimulationData,
  firestore: Firestore = db,
): Promise<SmileSimulationEntity> => {
  const parsedClinicId = parseRequiredId("clinicId", clinicId);
  const parsedSimulationId = parseRequiredId("simulationId", simulationId);
  const parsedData = SmileSimulationDataSchema.parse(data);
  const now = Timestamp.now();
  const simulation = SmileSimulationSchema.parse({
    clinicId: parsedClinicId,
    ...parsedData,
    createdAt: now,
    updatedAt: now,
  });

  await setDoc(
    smileSimulationDoc(firestore, parsedClinicId, parsedSimulationId),
    simulation,
  );

  return SmileSimulationEntitySchema.parse({
    id: parsedSimulationId,
    ...simulation,
  });
};

export const updateSmileSimulation = async (
  clinicId: string,
  simulationId: string,
  data: Partial<SmileSimulationData>,
  firestore: Firestore = db,
): Promise<SmileSimulationEntity> => {
  const parsedClinicId = parseRequiredId("clinicId", clinicId);
  const parsedSimulationId = parseRequiredId("simulationId", simulationId);
  const parsedData = SmileSimulationUpdateDataSchema.parse(data);
  const docRef = smileSimulationDoc(
    firestore,
    parsedClinicId,
    parsedSimulationId,
  );
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    throw new Error(
      `Smile simulation not found: ${smileSimulationDocPath(parsedClinicId, parsedSimulationId)}`,
    );
  }

  const nextSimulation = SmileSimulationSchema.parse({
    ...snapshot.data(),
    ...parsedData,
    updatedAt: Timestamp.now(),
  });

  await setDoc(docRef, nextSimulation);

  return SmileSimulationEntitySchema.parse({
    id: parsedSimulationId,
    ...nextSimulation,
  });
};

export const listSmileSimulationsByClinic = async (
  clinicId: string,
  limit?: number,
  firestore: Firestore = db,
): Promise<SmileSimulationEntity[]> => {
  const parsedClinicId = parseRequiredId("clinicId", clinicId);
  const parsedLimit = parseLimit(limit);
  const collectionRef = smileSimulationCollection(firestore, parsedClinicId);
  const snapshot =
    parsedLimit === undefined
      ? await getDocs(query(collectionRef, orderBy("createdAt", "desc")))
      : await getDocs(
          query(
            collectionRef,
            orderBy("createdAt", "desc"),
            limitTo(parsedLimit),
          ),
        );

  return snapshot.docs.map(toSmileSimulationEntity);
};

export const smileSimulationRepository = {
  get: getSmileSimulation,
  create: createSmileSimulation,
  update: updateSmileSimulation,
  listByClinic: listSmileSimulationsByClinic,
};
